import styles from './Quiz.module.scss'
import { Button, Heading, Icon } from '@boltenergy-be/design-system'
import { Fragment, useState } from 'react'
import {
  DynamicPriceQuizQuestions,
  FlowUserInputs,
  PossibleQuestions,
  PossibleResults,
  QuestionValue,
  QuizProps,
  StackingCardFlowAction
} from './types'
import classNames from 'classnames'
import { useTranslation } from 'next-i18next/pages'
import parse from 'html-react-parser'
import QuizActionButton from 'features/dynamic-tariff/components/quiz-action-button/QuizActionButton'

// Local variable for keeping the score (useState has delayed state update)
let results: FlowUserInputs<DynamicPriceQuizQuestions> = {}

const Quiz = <Q extends PossibleQuestions, R extends PossibleResults>({
  defaultResultCard,
  quizFlow,
  resultCards,
  questionCards,
  namespace
}: QuizProps<Q, R>) => {
  // i18n
  const { t } = useTranslation(namespace)

  // Local state
  const [resultCard, setResultCard] = useState<R>(defaultResultCard)

  // Constants
  const resultCardType = 'result-card'
  const resultRating = resultCards[resultCard].rating

  /**
   * Get the next card
   * @template Q
   * @template R
   * @param {StackingCardFlowAction<Q, R>} cardAction
   * @param {Q} question
   * @param {QuestionValue} [extraAnswer]
   * @returns {boolean}
   */
  const getNextCard = (cardAction: StackingCardFlowAction<Q, R>, question: Q, extraAnswer?: QuestionValue): string => {
    const updatedResults = extraAnswer ? { ...results, [question]: extraAnswer } : results
    const next = cardAction.next(updatedResults)
    const isResultCard = Object.keys(resultCards).includes(next)

    return isResultCard ? resultCardType : next
  }

  /**
   * Handle the next card action
   * @template Q
   * @template R
   * @param {StackingCardFlowAction<Q, R>} cardAction
   * @param {Q} question
   */
  const onHandleNext = (cardAction: StackingCardFlowAction<Q, R>, question: Q) => {
    results[question] = cardAction.value

    if (getNextCard(cardAction, question) === resultCardType) {
      const result = cardAction.next(results) as R
      setResultCard(result)
    }
  }

  return (
    <form className={styles['card-stack']}>
      {/* QUESTION CARDS */}
      {quizFlow.map((flowItem, index) => (
        <Fragment key={flowItem.question}>
          {/* INPUT */}
          <input id={flowItem.question} className={styles.input} name="card-set" type="radio" defaultChecked={index === 0} />

          {/* QUESTION CARD */}
          <div className={styles.card}>
            <div className={styles.content}>
              <small>
                {t('question')} {index + 1}/{quizFlow.length}
              </small>
              <Heading as="h3">{t(`quiz.question.${flowItem.question}.title`, questionCards[flowItem.question].title)}</Heading>

              <p>{parse(t(`quiz.question.${flowItem.question}.description`, questionCards[flowItem.question].description))}</p>

              <div className={styles['cta-group']}>
                <label
                  className={styles.positive}
                  htmlFor={getNextCard(flowItem.actions.yes, flowItem.question)}
                  onClick={() => onHandleNext(flowItem.actions.yes, flowItem.question)}
                >
                  <Icon name={flowItem.actions.yes.icon} />
                  {t('quiz.yes', flowItem.actions.yes.text)}
                </label>

                <label
                  className={styles.negative}
                  htmlFor={getNextCard(flowItem.actions.no, flowItem.question)}
                  onClick={() => onHandleNext(flowItem.actions.no, flowItem.question)}
                >
                  <Icon name={flowItem.actions.no.icon} />
                  {t('quiz.no', flowItem.actions.no.text)}
                </label>

                {flowItem.actions.maybe && (
                  <label
                    htmlFor={getNextCard(flowItem.actions.maybe, flowItem.question)}
                    onClick={() => flowItem.actions.maybe && onHandleNext(flowItem.actions.maybe, flowItem.question)}
                  >
                    <Icon name={flowItem.actions.maybe.icon} />
                    {t('quiz.maybe', flowItem.actions.maybe.text)}
                  </label>
                )}
              </div>
            </div>
          </div>
        </Fragment>
      ))}

      {/* RESULT CARD */}
      <input id="result-card" className={styles.input} name="card-set" type="radio" />
      <div className={classNames(styles.card, styles.result)}>
        <div className={styles.content}>
          <Heading as="h3" className={classNames(styles['result-title'], resultRating?.state && styles[resultRating.state])}>
            {parse(
              t(`quiz.result.${resultCard}.title`, resultCards[resultCard].title, {
                rating: t(`quiz.result.${resultCard}.rating`, { defaultValue: resultRating?.text })
              })
            )}
          </Heading>

          <p>{parse(t(`quiz.result.${resultCard}.description`, resultCards[resultCard].description))}</p>

          <div className={styles['cta-group']}>
            <QuizActionButton actionCard={resultCards[resultCard].actions.primary} {...{ namespace }}>
              {t(`quiz.result.${resultCard}.actions.primary`, resultCards[resultCard].actions.primary.text)}
            </QuizActionButton>

            {resultCards[resultCard].actions.secondary && (
              <QuizActionButton
                actionCard={resultCards[resultCard].actions.secondary}
                className={styles.link}
                variant="secondary"
                {...{ namespace }}
              >
                {t(`quiz.result.${resultCard}.actions.secondary`, resultCards[resultCard].actions.secondary.text)}
              </QuizActionButton>
            )}

            <Button
              type="reset"
              onClick={() => {
                results = {}
              }}
              variant="secondary"
              className={classNames(styles.link, styles.refresh)}
              leadingIcon="refresh"
            >
              {t('quiz.refresh')}
            </Button>
          </div>
        </div>
      </div>
    </form>
  )
}

export default Quiz
